import styled from 'styled-components';

export const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.width || '100%'};
  max-width: ${(props) => props.maxWidth || 'none'};
  min-width: 0;
  box-sizing: border-box;

  /* Prevent children from expanding beyond container */
  > label,
  > div {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
`;

export const Required = styled.span`
  color: #ef4444;
`;

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

export const ColorInput = styled.div`
  width: 100%;
  padding: 10px 14px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) => {
      if (props.error) return '#ef4444';
      if (props.isOpen) return props.theme?.colors?.primary || '#fa4616';
      return props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.15)';
    }};
  border-radius: 6px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#1F2937')};
  font-size: 14px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.15s;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  box-sizing: border-box;

  &:hover {
    ${(props) =>
      !props.disabled &&
      `
      background: ${
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(0, 0, 0, 0.02)'
      };
      border-color: ${
        props.isOpen
          ? props.theme?.colors?.primary || '#fa4616'
          : props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.2)'
      };
    `}
  }
`;

export const ColorSwatch = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.15)'};
  flex-shrink: 0;
  background: ${(props) =>
    props.color ||
    `repeating-linear-gradient(
      45deg,
      ${props.theme.name === 'dark' ? '#333' : '#ccc'},
      ${props.theme.name === 'dark' ? '#333' : '#ccc'} 5px,
      ${props.theme.name === 'dark' ? '#444' : '#ddd'} 5px,
      ${props.theme.name === 'dark' ? '#444' : '#ddd'} 10px
    )`};
`;

export const ColorName = styled.span`
  flex: 1;
  color: ${(props) =>
    props.hasValue
      ? props.theme.name === 'dark'
        ? '#ffffff'
        : '#1F2937'
      : props.theme.name === 'dark'
      ? '#64748b'
      : '#9CA3AF'};
`;

export const ChevronIcon = styled.span`
  color: ${(props) => (props.theme.name === 'dark' ? '#64748b' : '#9CA3AF')};
  transition: transform 0.15s ease-in-out;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 4px;
  box-sizing: border-box;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1a1a2e' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 8px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: ${(props) =>
    props.theme.name === 'dark'
      ? '0 4px 16px rgba(0, 0, 0, 0.5)'
      : '0 4px 16px rgba(0, 0, 0, 0.1)'};
`;

export const DropdownOption = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background 0.15s;
  color: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.85)' : '#374151'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)'};
  }

  ${(props) =>
    props.isSelected &&
    `
    background: ${
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.1)'
    };
    color: ${props.theme?.colors?.primary || '#fa4616'};
  `}

  ${(props) =>
    props.isHighlighted &&
    `
    background: ${
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)'
    };
  `}
`;

export const OptionName = styled.span`
  flex: 1;
`;

export const CheckIcon = styled.span`
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  display: flex;
  align-items: center;
  margin-left: auto;
`;

export const HelperText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => (props.error ? '#ef4444' : '#64748B')};
`;
